import type { Database } from '@/integrations/supabase/types';

export type FieldDataType = Database['public']['Enums']['field_data_type'];

export interface FieldLibraryItem {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  data_type: FieldDataType;
  format: string | null;
  pattern: string | null;
  min_length: number | null;
  max_length: number | null;
  minimum: number | null;
  maximum: number | null;
  is_common: boolean;
  display_order: number | null;
  example_value: string | null;
}

export interface FieldMapping {
  id?: string;
  field_id: string;
  field: FieldLibraryItem;
  is_required: boolean;
  default_value: string | null;
  lookup_group: string | null;
  inline_enum: string[] | null;
  display_order: number;
  notes: string | null;
}

export const TYPE_COLORS: Record<string, string> = {
  string: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200',
  number: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200',
  integer: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200',
  boolean: 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-200',
  array: 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-200',
  object: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/50 dark:text-cyan-200',
  null: 'bg-gray-100 text-gray-800 dark:bg-gray-900/50 dark:text-gray-200',
};

export const DATA_TYPE_OPTIONS: { value: FieldDataType; label: string }[] = [
  { value: 'string', label: 'String' },
  { value: 'number', label: 'Number' },
  { value: 'integer', label: 'Integer' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'array', label: 'Array' },
  { value: 'object', label: 'Object' },
  { value: 'null', label: 'Null' },
];

export const FORMAT_OPTIONS: Record<string, { value: string; label: string }[]> = {
  string: [
    { value: '', label: 'None' },
    { value: 'email', label: 'Email' },
    { value: 'date', label: 'Date (YYYY-MM-DD)' },
    { value: 'date-time', label: 'DateTime (ISO 8601)' },
    { value: 'time', label: 'Time (HH:MM:SS)' },
    { value: 'uuid', label: 'UUID' },
    { value: 'uri', label: 'URI/URL' },
    { value: 'phone', label: 'Phone (E.164)' },
  ],
  number: [
    { value: '', label: 'None' },
    { value: 'currency', label: 'Currency' },
    { value: 'percentage', label: 'Percentage' },
  ],
  integer: [
    { value: '', label: 'None' },
    { value: 'currency', label: 'Currency (cents)' },
  ],
};

export interface GenerateSchemaOptions {
  title?: string;
  description?: string;
  lookupData?: Record<string, string[]>;
}

// Generate JSON Schema from field mappings
export function generateSchema(
  fields: FieldMapping[],
  options?: GenerateSchemaOptions
): object {
  const properties: Record<string, object> = {};
  const required: string[] = [];

  fields.forEach((mapping) => {
    const field = mapping.field;
    const prop: Record<string, unknown> = {
      type: field.data_type,
      description: field.description || undefined,
    };

    // Add format if present
    if (field.format) prop.format = field.format;

    // Add constraints
    if (field.min_length !== null) prop.minLength = field.min_length;
    if (field.max_length !== null) prop.maxLength = field.max_length;
    if (field.minimum !== null) prop.minimum = field.minimum;
    if (field.maximum !== null) prop.maximum = field.maximum;
    if (field.pattern) prop.pattern = field.pattern;

    // Add enum - prioritize inline_enum, then lookup_group
    if (mapping.inline_enum && mapping.inline_enum.length > 0) {
      prop.enum = mapping.inline_enum;
    } else if (mapping.lookup_group && options?.lookupData?.[mapping.lookup_group]) {
      prop.enum = options.lookupData[mapping.lookup_group];
    }

    properties[field.slug] = prop;

    if (mapping.is_required) {
      required.push(field.slug);
    }
  });

  return {
    $schema: 'http://json-schema.org/draft-07/schema#',
    type: 'object',
    title: options?.title,
    description: options?.description,
    properties,
    required: required.length > 0 ? required : undefined,
  };
}

// Generate example JSON from field mappings
// Priority: 1. default_value, 2. inline_enum, 3. lookup_group, 4. field.example_value, 5. type fallback
export function generateExample(
  fields: FieldMapping[],
  lookupData?: Record<string, string[]>
): object {
  const example: Record<string, unknown> = {};

  fields.forEach((mapping) => {
    const field = mapping.field;

    // 1. Use default value if set on the mapping
    if (mapping.default_value) {
      example[field.slug] = parseValue(mapping.default_value, field.data_type);
      return;
    }

    // 2. Use first inline enum value if available
    if (mapping.inline_enum && mapping.inline_enum.length > 0) {
      example[field.slug] = mapping.inline_enum[0];
      return;
    }
    
    // 3. Use first lookup value if available
    if (mapping.lookup_group && lookupData?.[mapping.lookup_group]?.length) {
      example[field.slug] = lookupData[mapping.lookup_group][0];
      return;
    }

    // 4. Use field's own example_value from database (primary source for realistic examples)
    if (field.example_value) {
      example[field.slug] = parseValue(field.example_value, field.data_type);
      return;
    }

    // 5. Type-based fallback (minimal, last resort)
    example[field.slug] = getTypeFallback(field.data_type);
  });

  return example;
}

// Minimal type-based fallback values - used only when no database example exists
function getTypeFallback(dataType: FieldDataType): unknown {
  switch (dataType) {
    case 'string':
      return '';
    case 'number':
      return 0;
    case 'integer':
      return 0;
    case 'boolean':
      return true;
    case 'array':
      return [];
    case 'object':
      return {};
    default:
      return null;
  }
}

function parseValue(value: string, dataType: FieldDataType): unknown {
  switch (dataType) {
    case 'number':
      return parseFloat(value) || 0;
    case 'integer':
      return parseInt(value, 10) || 0;
    case 'boolean':
      return value.toLowerCase() === 'true';
    case 'array':
    case 'object':
      // Try to parse JSON for complex types
      try {
        return JSON.parse(value);
      } catch {
        return dataType === 'array' ? [] : {};
      }
    default:
      return value;
  }
}

// Generate fields array for legacy compatibility
export function generateFieldsArray(mappings: FieldMapping[]): object[] {
  return mappings.map((mapping) => ({
    name: mapping.field.slug,
    type: mapping.field.format
      ? `${mapping.field.data_type} (${mapping.field.format})`
      : mapping.field.data_type,
    description: mapping.field.description || '',
    required: mapping.is_required,
    example: mapping.default_value || undefined,
  }));
}
